/**
 * Parwaaz backend — the application workflow state machine.
 *
 * Pure functions only: given an application, an action, the actor's role and a payload,
 * compute the patch to apply and the audit reason. No I/O — handlers.ts is the only place
 * that talks to the Store, inside the platform lock.
 */
import {
  APPLICATION_STATES,
  type Application,
  type ApplicationState,
  type CaseActionName,
  type Finding,
  type ISOTimestamp,
  type RiskTier,
  type Role,
  riskTier as computeRiskTier,
  correctionItems,
} from '@parwaaz/shared';
import { ForbiddenError, IllegalTransitionError, ValidationError } from './errors';

export interface CaseActionPayload {
  assignTo?: string;
  note?: string;
  correctionItems?: string[];
  correctionMessage?: string;
  outcome?: 'pass' | 'fail' | 'conditional';
  checklist?: Record<string, boolean>;
  notes?: string;
  recommendation?: 'approve' | 'reject';
  rule?: string;
  printSheet?: string;
}

export interface TransitionResult {
  patch: Partial<Application>;
  fromState: ApplicationState;
  toState: ApplicationState;
  reason: string;
}

/** States with no further case action defined anywhere in the table. */
const TERMINAL_STATES: readonly ApplicationState[] = ['Expired'];

export function isTerminal(state: ApplicationState): boolean {
  return TERMINAL_STATES.includes(state);
}

const NON_TERMINAL: readonly ApplicationState[] = APPLICATION_STATES.filter((s) => !isTerminal(s));

const ROLES_FOR: Record<CaseActionName, readonly Role[]> = {
  assign: ['assistant', 'sdm', 'admin'],
  note: ['evaluator', 'assistant', 'sdm', 'dc', 'admin'],
  requestCorrection: ['assistant', 'sdm', 'admin'],
  evaluate: ['evaluator', 'sdm', 'admin'],
  recommend: ['assistant', 'sdm', 'admin'],
  approve: ['sdm', 'admin'],
  reject: ['sdm', 'admin'],
  revoke: ['sdm', 'admin'],
  reissue: ['sdm', 'admin'],
  markPrinted: ['assistant', 'sdm', 'admin'],
  override: ['sdm', 'admin'],
  reopen: ['sdm', 'admin'],
};

const FROM_STATES: Record<CaseActionName, readonly ApplicationState[]> = {
  assign: NON_TERMINAL,
  note: APPLICATION_STATES,
  requestCorrection: ['Submitted', 'Validated', 'ReadyForEvaluation', 'UnderReview'],
  evaluate: ['ReadyForEvaluation'],
  recommend: ['Evaluated'],
  approve: ['UnderReview', 'Evaluated'],
  reject: ['Submitted', 'Validated', 'CorrectionRequested', 'ReadyForEvaluation', 'Evaluated', 'EvaluationFailed', 'UnderReview'],
  revoke: ['CardIssued'],
  reissue: ['Revoked', 'CardIssued'],
  markPrinted: ['CardIssued'],
  override: NON_TERMINAL,
  reopen: ['Rejected', 'EvaluationFailed'],
};

/** Actions a given role may legally take on this application right now. */
export function allowedActions(app: Application, role: Role): CaseActionName[] {
  return (Object.keys(ROLES_FOR) as CaseActionName[]).filter(
    (action) => ROLES_FOR[action].includes(role) && FROM_STATES[action].includes(app.status),
  );
}

export function canTransition(app: Application, action: CaseActionName, role: Role): boolean {
  return allowedActions(app, role).includes(action);
}

function requireReason(reason: string | undefined, action: string): string {
  if (!reason || !reason.trim()) throw new ValidationError(`a reason is required for ${action}`);
  return reason.trim();
}

/**
 * Applies one case action to an application. Throws ForbiddenError (role), IllegalTransitionError
 * (wrong state) or ValidationError (missing payload) — never mutates `app`.
 */
export function applyTransition(
  app: Application,
  action: CaseActionName,
  role: Role,
  actor: string,
  payload: CaseActionPayload,
  reasonInput: string | undefined,
  now: ISOTimestamp,
): TransitionResult {
  if (!ROLES_FOR[action].includes(role)) {
    throw new ForbiddenError(`role '${role}' may not perform '${action}'`);
  }
  if (!FROM_STATES[action].includes(app.status)) {
    throw new IllegalTransitionError(`'${action}' cannot be applied to an application in state '${app.status}'`);
  }

  const fromState = app.status;

  switch (action) {
    case 'assign': {
      if (!payload.assignTo) throw new ValidationError('assignTo is required');
      return { patch: { assignedTo: payload.assignTo }, fromState, toState: fromState, reason: `assigned to ${payload.assignTo}` };
    }

    case 'note': {
      if (!payload.note || !payload.note.trim()) throw new ValidationError('note text is required');
      const notes = [...(app.notes ?? []), { by: actor, at: now, text: payload.note.trim() }];
      return { patch: { notes }, fromState, toState: fromState, reason: 'note added' };
    }

    case 'requestCorrection': {
      const items = payload.correctionItems ?? [];
      if (items.length === 0) throw new ValidationError('correctionItems is required');
      const patch: Partial<Application> = {
        status: 'CorrectionRequested',
        correctionRequest: { items, message: payload.correctionMessage ?? '', requestedBy: actor, at: now },
      };
      return { patch, fromState, toState: 'CorrectionRequested', reason: `correction requested: ${items.join('; ')}` };
    }

    case 'evaluate': {
      if (!payload.outcome) throw new ValidationError('outcome is required');
      if (!payload.checklist) throw new ValidationError('checklist is required');
      const toState: ApplicationState = payload.outcome === 'fail' ? 'EvaluationFailed' : 'Evaluated';
      const patch: Partial<Application> = {
        status: toState,
        evaluatorUser: actor,
        assessmentOutcome: payload.outcome,
        assessmentChecklist: payload.checklist,
        assessmentNotes: payload.notes,
        assessedAt: now,
      };
      return { patch, fromState, toState, reason: `evaluated: ${payload.outcome}` };
    }

    case 'recommend': {
      if (!payload.recommendation) throw new ValidationError('recommendation is required');
      const patch: Partial<Application> = {
        status: 'UnderReview',
        reviewerUser: actor,
        recommendation: payload.recommendation,
        reviewedAt: now,
      };
      return { patch, fromState, toState: 'UnderReview', reason: `recommended: ${payload.recommendation}` };
    }

    case 'approve': {
      if (app.riskTier === 'red') {
        const unresolved = app.findings.filter((f) => f.severity === 'fail' && !f.overridden);
        if (unresolved.length > 0) {
          throw new ForbiddenError(
            `cannot approve: unresolved red findings — ${unresolved.map((f) => f.rule).join(', ')}`,
          );
        }
      }
      const patch: Partial<Application> = {
        status: 'Approved',
        approverUser: actor,
        decision: 'approved',
        decisionReason: reasonInput,
        decidedAt: now,
      };
      return { patch, fromState, toState: 'Approved', reason: reasonInput ?? 'approved' };
    }

    case 'reject': {
      const reason = requireReason(reasonInput, 'reject');
      const patch: Partial<Application> = {
        status: 'Rejected',
        approverUser: actor,
        decision: 'rejected',
        decisionReason: reason,
        decidedAt: now,
      };
      return { patch, fromState, toState: 'Rejected', reason };
    }

    case 'revoke': {
      const reason = requireReason(reasonInput, 'revoke');
      if (!app.card) throw new ValidationError('no card to revoke');
      const patch: Partial<Application> = {
        status: 'Revoked',
        card: { ...app.card, revokedAt: now, revocationReason: reason },
      };
      return { patch, fromState, toState: 'Revoked', reason };
    }

    case 'reissue': {
      // The new card is allocated later via allocateCard (Approved-only). We leave the old
      // card record on the application untouched so allocateCard can read its cardNo into
      // the new card's reissuedFrom; handlers.ts moves the old record into the cards list.
      // Mark the old card revoked on the application too, so status/case views never show it as
      // still valid while the replacement is being allocated. allocateCard reads cardNo → reissuedFrom.
      const patch: Partial<Application> = {
        status: 'Approved',
        card: app.card ? { ...app.card, revokedAt: app.card.revokedAt ?? now, revocationReason: app.card.revocationReason ?? (reasonInput ?? 'reissued') } : app.card,
      };
      return { patch, fromState, toState: 'Approved', reason: reasonInput ?? 'reissue' };
    }

    case 'markPrinted': {
      if (!app.card) throw new ValidationError('no card to mark printed');
      const patch: Partial<Application> = {
        card: { ...app.card, printedAt: now, printSheet: payload.printSheet ?? '' },
      };
      return { patch, fromState, toState: fromState, reason: 'marked printed' };
    }

    case 'override': {
      if (!payload.rule) throw new ValidationError('rule is required');
      const reason = requireReason(reasonInput, 'override');
      let matched = false;
      const findings: Finding[] = app.findings.map((f) => {
        if (f.rule !== payload.rule) return f;
        matched = true;
        return { ...f, overridden: { by: actor, reason, at: now } };
      });
      if (!matched) throw new ValidationError(`no finding for rule '${payload.rule}'`);
      // riskTier() itself doesn't know about overrides — exclude overridden fails from the tier
      // calculation while leaving them (marked) in the findings list the officer sees.
      const forTier = findings.filter((f) => !(f.severity === 'fail' && f.overridden));
      const patch: Partial<Application> = { findings, riskTier: computeRiskTier(forTier) };
      return { patch, fromState, toState: fromState, reason: `overrode ${payload.rule}: ${reason}` };
    }

    case 'reopen': {
      const patch: Partial<Application> = { status: 'Submitted' };
      return { patch, fromState, toState: 'Submitted', reason: reasonInput ?? 'reopened' };
    }

    default: {
      const exhaustive: never = action;
      throw new IllegalTransitionError(`unknown action '${exhaustive}'`);
    }
  }
}

/**
 * The automatic advance that follows rules evaluation, used by both `submit` and
 * `attachCorrection`. Conceptually the application passes through Validated and, if the tier
 * is not red, lands immediately on ReadyForEvaluation (the evaluator queue); if red it stays
 * on Submitted with a system-authored correction request.
 */
export function advanceAfterValidation(
  tier: RiskTier,
  findings: Finding[],
  now: ISOTimestamp,
): { status: ApplicationState; correctionRequest: Application['correctionRequest'] } {
  if (tier === 'red') {
    return {
      status: 'Submitted',
      correctionRequest: {
        items: correctionItems(findings),
        message: 'Automatically generated from validation findings.',
        requestedBy: 'system',
        at: now,
      },
    };
  }
  return { status: 'ReadyForEvaluation', correctionRequest: null };
}
